import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Container,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { useDispatch, useSelector } from "react-redux";
import {
  handleAddCatalog,
  selectIsAddEditOpen,
  selectEditCatalogData,
  selectIsEditMode,
  selectCatalogType,
  selectAddEditConnectionInfo,
  setCatalogDataField,
  selectEditCatalogId,
} from "@/features/connections/manageCatalogDataSlice";
import { ViewCustomFields } from "../CatalogCustomFieldPopup";
import { CatalogTypes, CatalogTypesExt } from "../MaintenanceTypes";
import AddTaxGroupPopup from "./AddTaxGroupPopup";
import AddWarehousePopup from "./AddWarehousePopup";
import AddShipToPopup from "./AddShipToPopup";
import AddCustomerPopup from "./AddCustomerPopup";
import AddCarrierPopup from "./AddCarrierPopup";
import AddAllowanceChargePopup from "./AddAllowanceChargePopup";
import AddItemPopup from "./AddItemPopup";
import AddItemUOMPopup from "./AddItemUOMPopup";
import {
  resetAddEditPopUp,
  updateCatalogData,
} from "../../../features/connections/manageCatalogDataSlice";

const AddEditCatalogPopup = ({ 
  onClose, 
  onValidate, 
  onComplete, 
  children 
}) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectIsAddEditOpen);
  const isEdit = useSelector(selectIsEditMode);
  const selectedCatalogType = useSelector(selectCatalogType);
  const catalogData = useSelector(selectEditCatalogData);
  const connectionInfo = useSelector(selectAddEditConnectionInfo);
  const editCatalogId = useSelector(selectEditCatalogId);

  const handleClose = () => {
    onClose();
  };
  const handleChange = (event) => {
    const { name, value } = event.target;
    dispatch(setCatalogDataField({ name, value }));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={"xl"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          {(isEdit ? "Edit" : "Add") +
            " Catalog - " +
            (Object.values(CatalogTypesExt).find(
              (x) => x.Enum === selectedCatalogType,
            )?.Display ?? "") +
            " for " +
            connectionInfo.displayName}
        </div>
        <IconButton
          onClick={handleClose}
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        <Container>
          <Grid
            container
            spacing={2}
            rowSpacing={1}
            style={{ marginBottom: "20px", marginTop: "20px" }}
            sx={{
              px: 20,
            }}
          >
            {/*custom <grid item/>s sent from catalog specific component*/}
            {React.Children.map(children, (child) =>
              React.isValidElement(child)
                ? React.cloneElement(child, {
                    //any extra props
                  })
                : child,
            )}
            <Grid item xs={12} sx={{ paddingBottom: "30px" }} />
            <Grid item xs={4}>
              <Typography variant="subheading2">Custom Fields</Typography>
            </Grid>
            <Grid item xs={12}>
              <ViewCustomFields
                canSet={true}
                catalogType={selectedCatalogType}
                onValueChange={(customField) => {
                  const newCustomFields = [
                    ...(catalogData?.CustomFields ?? []).filter(
                      (x) => x.CatalogCustomFieldId !== customField.id,
                    ),
                    {
                      CatalogCustomFieldId: customField.id,
                      FieldCode: customField.code,
                      FieldValue: customField.value,
                    },
                  ];

                  handleChange({
                    target: { name: "CustomFields", value: newCustomFields },
                  });
                }}
                existingFields={catalogData?.CustomFields ?? []}
              />
            </Grid>
          </Grid>
        </Container>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        <ShepButton
          onClick={async () => {
            if (isEdit) {
              await dispatch(
                updateCatalogData({
                  validateForm: onValidate,
                  handleClose,
                  dispatch,
                  selectedConnection: connectionInfo.connectionId,
                  catalogData: JSON.stringify({ ...catalogData }),
                  selectedCatalogType: selectedCatalogType,
                  artifactId: editCatalogId,
                  itemQualifiers: catalogData.ItemQualifiers,
                }),
              );
            } else {
              await handleAddCatalog(
                onValidate,
                handleClose,
                dispatch,
                connectionInfo.connectionId,
                selectedCatalogType,
                JSON.stringify({ ...catalogData }),
                catalogData.ItemQualifiers,
              );
            }

            onComplete();
          }}
        >
          {isEdit ? "Save" : "Add"}
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

const CatalogPopup = ({ onClose, onValidate = null, onComplete }) => {
  const dispatch = useDispatch();
  const catalogData = useSelector(selectEditCatalogData);
  const selectedCatalogType = useSelector(selectCatalogType);

  useEffect(() => {
    if (
      selectedCatalogType === -1 &&
      catalogData &&
      Object.keys(catalogData).length > 1
    ) {
      dispatch(resetAddEditPopUp());
    }
  });

  const handleClose = () => {
    if (onClose) onClose();
    //clean up for fresh slate when reopened elsewhere in Shep
    dispatch(resetAddEditPopUp());
  };

  return (
    <>
      {selectedCatalogType === CatalogTypes.TaxGroup && (
        <AddTaxGroupPopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.Warehouse && (
        <AddWarehousePopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.ShipTo && (
        <AddShipToPopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.Customer && (
        <AddCustomerPopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.Item && (
        <AddItemPopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.ItemUOM && (
        <AddItemUOMPopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.Carrier && (
        <AddCarrierPopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
      {selectedCatalogType === CatalogTypes.AllowanceOrCharge && (
        <AddAllowanceChargePopup
          onClose={handleClose}
          onValidate={onValidate}
          onComplete={onComplete}
        />
      )}
    </>
  );
};

export { AddEditCatalogPopup, CatalogPopup };
